import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import { FiX } from "react-icons/fi";
import { Language } from "../App";
import MixpanelManager from "../constants/MixpanelManager";
import { MixpanelEvents } from "../constants/MixpanelEvents";
import "./ReportModal.css";

const ReportModal = ({
  isOpen,
  show,
  toggle,
  onClose,
  handleReport,
  onSubmit,
  onConfirm,
  isLoading,
  loading,
  heading,
  title,
  type = "event",
  reportType,
}) => {
  const { lang } = useContext(Language) || {};
  const isModalOpen = Boolean(isOpen ?? show);
  const isSubmitting = Boolean(isLoading ?? loading);

  // Close handler supporting multiple signature types
  const handleClose = () => {
    if (typeof toggle === "function") {
      toggle(false);
    } else if (typeof onClose === "function") {
      onClose();
    }
  };

  // Submit callback
  const submitCallback = handleReport || onSubmit || onConfirm;

  // Contextual item calculation
  const normalizedType = String(reportType || type || "").toLowerCase();
  const headingStr = String(heading || title || "").toLowerCase();

  const isWebinar = normalizedType.includes("webinar") || headingStr.includes("webinar");
  const isSession =
    normalizedType.includes("session") ||
    normalizedType.includes("clinical") ||
    headingStr.includes("session") ||
    headingStr.includes("clinical");
  const isPost = normalizedType.includes("post") || headingStr.includes("post");
  const isCourse = normalizedType.includes("course") || headingStr.includes("course");

  // Determine Title
  const modalTitle =
    heading ||
    title ||
    (isWebinar
      ? lang?.report_webinar || "Report this webinar"
      : isSession
      ? lang?.report_clinical_session || "Report this session"
      : isPost
      ? lang?.report_post || "Report this post"
      : isCourse
      ? lang?.report_course || "Report this course"
      : lang?.report_event || "Report this event");

  // Determine Subtitle
  const modalSubtitle =
    lang?.report_confidential_desc ||
    lang?.tell_us_whats_wrong ||
    "Tell us what's wrong. Reports are confidential.";

  // Contextual fourth option
  const contextualOption = useMemo(() => {
    if (isWebinar) return lang?.report_reason_fake_webinar || "Not a real medical webinar";
    if (isSession) return lang?.report_reason_fake_session || "Not a real clinical session";
    if (isPost) return lang?.report_reason_fake_post || "Not a real medical post";
    if (isCourse) return lang?.report_reason_fake_course || "Not a real medical course";
    return lang?.report_reason_fake_event || "Not a real medical event";
  }, [isWebinar, isSession, isPost, isCourse, lang]);

  // Report Reasons matching Figma
  const reportOptions = useMemo(
    () => [
      lang?.report_reason_misleading || "Misleading or false information",
      lang?.report_reason_spam || "Spam or scam",
      lang?.report_reason_inappropriate || "Inappropriate or offensive content",
      contextualOption,
      lang?.report_reason_something_else || "Something else",
    ],
    [contextualOption, lang]
  );

  const [selectedReason, setSelectedReason] = useState(reportOptions[0]);
  const [details, setDetails] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSelectedReason(reportOptions[0]);
      setDetails("");
    }
  }, [isModalOpen, reportOptions]);

  const handleSubmit = () => {
    const trimmedDetails = details.trim();
    let finalReport = selectedReason || reportOptions[0];

    if (trimmedDetails) {
      finalReport = selectedReason ? `${selectedReason}: ${trimmedDetails}` : trimmedDetails;
    }

    try {
      if (MixpanelManager?.track && MixpanelEvents?.courseReported) {
        MixpanelManager.track(MixpanelEvents.courseReported, {
          report_text: finalReport,
          heading: modalTitle,
        });
      }
    } catch (e) {
      // ignore mixpanel tracking failures
    }

    if (typeof submitCallback === "function") {
      submitCallback(finalReport);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      toggle={handleClose}
      centered
      dialogClassName="report-modal-dialog"
      contentClassName="report-modal-content"
      fade={true}
      zIndex={9999}
      backdrop="static"
    >
      <div className="report-modal-card">
        {/* Close Button (Frame 1686558049) */}
        <button
          type="button"
          className="report-modal-close-btn"
          onClick={handleClose}
          aria-label="Close"
          disabled={isSubmitting}
        >
          <FiX size={14} />
        </button>

        {/* Header (Frame 1686558079) */}
        <div className="report-modal-header">
          <h2 className="report-modal-title">{modalTitle}</h2>
          <p className="report-modal-subtitle">{modalSubtitle}</p>
        </div>

        {/* Radio Options List (Frame 2147232894) */}
        <div className="report-modal-options">
          {reportOptions.map((option, index) => {
            const isSelected = selectedReason === option;
            return (
              <button
                key={index}
                type="button"
                className="report-modal-option-row"
                onClick={() => setSelectedReason(option)}
              >
                <div className={`report-modal-radio-icon ${isSelected ? "active" : ""}`}>
                  {isSelected && <div className="report-modal-radio-dot" />}
                </div>
                <span className={`report-modal-option-label ${isSelected ? "active" : ""}`}>
                  {option}
                </span>
              </button>
            );
          })}
        </div>

        {/* Textarea Details (Group 1000002351 & Frame 1686558070) */}
        <div className="report-modal-details-wrap">
          <textarea
            className="report-modal-textarea"
            placeholder={lang?.report_add_details_optional || "Add more details (optional)"}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons (Frame 2147232891) */}
        <div className="report-modal-actions">
          {/* Cancel (Frame 2147232889) */}
          <button
            type="button"
            className="report-modal-cancel-btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {lang?.cancel || "Cancel"}
          </button>

          {/* Submit Report (Frame 2147232890) */}
          <button
            type="button"
            className="report-modal-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner size="sm" color="light" />
            ) : (
              lang?.submit_report || "Submit Report"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportModal;
